import React, { useState, useRef, useEffect } from 'react';
import { authAPI, setToken } from './SellerApi';
import './SellerPortal.css';

const STEPS_REG = ['mobile', 'otp', 'details', 'business'];

const SellerAuth = ({ onAuthSuccess }) => {
  const [mode,    setMode]    = useState('login');   // 'login' | 'register'
  const [step,    setStep]    = useState('mobile');  // mobile | otp | details | business
  const [mobile,  setMobile]  = useState('');
  const [otp,     setOtp]     = useState(['','','','','','']);
  const [timer,   setTimer]   = useState(0);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');
  const otpRefs = useRef([]);
  const timerRef = useRef(null);

  // Registration form data
  const [form, setForm] = useState({
    firstName: '', lastName: '', email: '', mobile: '',
    gst: '', pan: '',
    businessName: '', businessCategory: 'Vegetables',
    addressLine1: '', addressLine2: '', city: '', state: 'Tamil Nadu', pincode: '',
  });

  useEffect(() => {
    if (timer > 0) { timerRef.current = setTimeout(() => setTimer(t => t - 1), 1000); }
    return () => clearTimeout(timerRef.current);
  }, [timer]);

  const upForm = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // ── SEND OTP ──
  const handleSendOtp = async (e) => {
    e.preventDefault();
    const num = mode === 'register' ? form.mobile : mobile;
    if (!/^\d{10}$/.test(num)) { setError('Enter a valid 10-digit mobile number.'); return; }
    setError(''); setLoading(true);
    try {
      await authAPI.sendOtp(num);
      setStep('otp'); setTimer(30);
      setTimeout(() => otpRefs.current[0]?.focus(), 100);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  // ── VERIFY OTP ──
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    const code = otp.join('');
    if (code.length < 6) { setError('Enter all 6 digits.'); return; }
    setError(''); setLoading(true);
    try {
      const num = mode === 'register' ? form.mobile : mobile;
      if (mode === 'login') {
        const res = await authAPI.verifyOtp(num, code);
        setToken(res.token);
        localStorage.setItem('seller_user', JSON.stringify(res.seller));
        onAuthSuccess(res.seller);
      } else {
        // For register, proceed to details step after OTP
        setStep('details');
      }
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  // ── COMPLETE REGISTRATION ──
  const handleRegister = async (e) => {
    e.preventDefault();
    if (!form.pan) { setError('PAN number is required.'); return; }
    setError(''); setLoading(true);
    try {
      const res = await authAPI.register({ ...form, otp: otp.join('') });
      setToken(res.token);
      localStorage.setItem('seller_user', JSON.stringify(res.seller));
      onAuthSuccess(res.seller);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const handleOtpChange = (idx, val) => {
    if (!/^\d?$/.test(val)) return;
    const next = [...otp]; next[idx] = val; setOtp(next);
    if (val && idx < 5) otpRefs.current[idx + 1]?.focus();
    if (!val && idx > 0) otpRefs.current[idx - 1]?.focus();
  };

  const handleResend = async () => {
    if (timer > 0) return;
    setLoading(true);
    try {
      await authAPI.sendOtp(mode === 'register' ? form.mobile : mobile);
      setTimer(30); setOtp(['','','','','','']);
      otpRefs.current[0]?.focus();
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const stepIdx = mode === 'login' ? 0 : STEPS_REG.indexOf(step);

  return (
    <div className="sa-root">
      {/* ── LEFT VISUAL ── */}
      <div className="sa-visual">
        <div className="sa-visual-inner">
          <div className="sa-logo-mark">
            <div className="sa-logo-hex">A</div>
            <span className="sa-logo-text">ATR SELLER HUB</span>
          </div>

          <h1 className="sa-visual-headline">
            Sell directly to<br />
            <span className="em">50,000+ customers</span><br />
            across India.
          </h1>
          <p className="sa-visual-sub">
            India's fastest-growing agro marketplace. Zero listing fees.
            Weekly payouts. Full logistics support.
          </p>

          <div className="sa-stat-grid">
            <div className="sa-stat-box"><span className="sa-stat-val">50K+</span><span className="sa-stat-lbl">Active Buyers</span></div>
            <div className="sa-stat-box"><span className="sa-stat-val">₹12Cr+</span><span className="sa-stat-lbl">Monthly Payouts</span></div>
            <div className="sa-stat-box"><span className="sa-stat-val">4.8★</span><span className="sa-stat-lbl">Seller Rating</span></div>
          </div>

          <div className="sa-benefits">
            {['Zero commission on first 3 months', 'Weekly earnings deposited directly', 'Dedicated seller support manager', 'Real-time order & inventory tracking'].map(b => (
              <div key={b} className="sa-benefit">
                <div className="sa-check">✓</div>
                <span>{b}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── RIGHT FORM ── */}
      <div className="sa-form-panel">
        <div className="sa-form-card">

          {/* Step progress dots */}
          {mode === 'register' && (
            <div className="sa-step-tabs">
              {STEPS_REG.map((s, i) => (
                <div key={s} className={`sa-step-tab ${i < stepIdx ? 'done' : i === stepIdx ? 'active' : ''}`} />
              ))}
            </div>
          )}

          <h2 className="sa-form-title">
            {step === 'mobile'   ? (mode === 'login' ? 'Welcome back' : 'Create your store')
            : step === 'otp'    ? 'Verify number'
            : step === 'details' ? 'Your details'
            :                     'Business info'}
          </h2>
          <p className="sa-form-sub">
            {step === 'mobile'   ? (mode === 'login' ? 'Login to your seller account' : 'Step 1 of 4 — Enter your mobile')
            : step === 'otp'    ? `OTP sent to +91 ${mode === 'register' ? form.mobile : mobile}`
            : step === 'details' ? 'Step 3 of 4 — Personal information'
            :                     'Step 4 of 4 — Business & address'}
          </p>

          {error && <div className="sa-error">{error}</div>}

          {/* ── STEP: MOBILE (Login) ── */}
          {step === 'mobile' && mode === 'login' && (
            <form onSubmit={handleSendOtp}>
              <div className="sa-input-group">
                <label className="sa-label">MOBILE NUMBER</label>
                <div className="sa-mobile-wrap">
                  <span className="sa-prefix">+91</span>
                  <input type="tel" maxLength={10} placeholder="10-digit number" value={mobile} onChange={e => setMobile(e.target.value.replace(/\D/g,''))} required />
                </div>
              </div>
              <button className="sa-submit" type="submit" disabled={loading}>{loading ? 'Sending…' : 'Send OTP →'}</button>
              <p className="sa-toggle">New seller? <span onClick={() => { setMode('register'); setStep('mobile'); setError(''); }}>Register your store</span></p>
            </form>
          )}

          {/* ── STEP: MOBILE (Register) ── */}
          {step === 'mobile' && mode === 'register' && (
            <form onSubmit={handleSendOtp}>
              <div className="sa-input-group">
                <label className="sa-label">MOBILE NUMBER</label>
                <div className="sa-mobile-wrap">
                  <span className="sa-prefix">+91</span>
                  <input type="tel" maxLength={10} placeholder="10-digit number" value={form.mobile} onChange={e => upForm('mobile', e.target.value.replace(/\D/,''))} required />
                </div>
              </div>
              <button className="sa-submit" type="submit" disabled={loading}>{loading ? 'Sending…' : 'Get OTP →'}</button>
              <p className="sa-toggle">Already registered? <span onClick={() => { setMode('login'); setStep('mobile'); setError(''); }}>Login</span></p>
            </form>
          )}

          {/* ── STEP: OTP ── */}
          {step === 'otp' && (
            <form onSubmit={handleVerifyOtp}>
              <div className="sa-input-group">
                <label className="sa-label">ENTER 6-DIGIT OTP</label>
                <div className="sa-otp-boxes">
                  {otp.map((d, i) => (
                    <input key={i} ref={el => otpRefs.current[i] = el} className="sa-otp-box" type="tel" maxLength={1} value={d}
                      onChange={e => handleOtpChange(i, e.target.value)}
                      onKeyDown={e => e.key === 'Backspace' && !otp[i] && i > 0 && otpRefs.current[i-1]?.focus()}
                    />
                  ))}
                </div>
                <div className="sa-resend">
                  {timer > 0 ? `Resend in ${timer}s` : <><span onClick={handleResend}>Resend OTP</span></>}
                </div>
              </div>
              <button className="sa-submit" type="submit" disabled={loading}>
                {loading ? 'Verifying…' : mode === 'login' ? 'Verify & Login →' : 'Verify & Continue →'}
              </button>
              <p className="sa-toggle"><span onClick={() => { setStep('mobile'); setOtp(['','','','','','']); setError(''); }}>← Change number</span></p>
            </form>
          )}

          {/* ── STEP: PERSONAL DETAILS ── */}
          {step === 'details' && (
            <form onSubmit={e => { e.preventDefault(); setStep('business'); setError(''); }}>
              <div className="sa-input-row">
                <div className="sa-input-group">
                  <label className="sa-label">FIRST NAME</label>
                  <input className="sa-input" type="text" placeholder="First name" value={form.firstName} onChange={e => upForm('firstName', e.target.value)} required />
                </div>
                <div className="sa-input-group">
                  <label className="sa-label">LAST NAME</label>
                  <input className="sa-input" type="text" placeholder="Last name" value={form.lastName} onChange={e => upForm('lastName', e.target.value)} required />
                </div>
              </div>
              <div className="sa-input-group">
                <label className="sa-label">EMAIL ADDRESS</label>
                <input className="sa-input" type="email" placeholder="you@example.com" value={form.email} onChange={e => upForm('email', e.target.value)} required />
              </div>
              <div className="sa-input-group">
                <label className="sa-label">PAN NUMBER <span style={{color:'#ef4444',fontSize:10}}>(Required)</span></label>
                <input className="sa-input" type="text" placeholder="ABCDE1234F" maxLength={10} value={form.pan} onChange={e => upForm('pan', e.target.value.toUpperCase())} required />
              </div>
              <div className="sa-input-group">
                <label className="sa-label">GST NUMBER <span className="sa-optional-tag">(Optional)</span></label>
                <input className="sa-input" type="text" placeholder="22AAAAA0000A1Z5" maxLength={15} value={form.gst} onChange={e => upForm('gst', e.target.value.toUpperCase())} />
              </div>
              <button className="sa-submit" type="submit">Continue →</button>
            </form>
          )}

          {/* ── STEP: BUSINESS DETAILS ── */}
          {step === 'business' && (
            <form onSubmit={handleRegister}>
              <div className="sa-input-group">
                <label className="sa-label">BUSINESS / FARM NAME</label>
                <input className="sa-input" type="text" placeholder="e.g. ATR Madurai Farms" value={form.businessName} onChange={e => upForm('businessName', e.target.value)} required />
              </div>
              <div className="sa-input-group">
                <label className="sa-label">CATEGORY</label>
                <select className="sa-input" value={form.businessCategory} onChange={e => upForm('businessCategory', e.target.value)}>
                  {['Vegetables','Fruits','Seeds','Grains','Spices','Dairy','Equipment','Others'].map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div className="sa-input-group">
                <label className="sa-label">ADDRESS LINE 1</label>
                <input className="sa-input" type="text" placeholder="Door no, Street" value={form.addressLine1} onChange={e => upForm('addressLine1', e.target.value)} required />
              </div>
              <div className="sa-input-group">
                <label className="sa-label">ADDRESS LINE 2 <span className="sa-optional-tag">(Optional)</span></label>
                <input className="sa-input" type="text" placeholder="Landmark, Area" value={form.addressLine2} onChange={e => upForm('addressLine2', e.target.value)} />
              </div>
              <div className="sa-input-row">
                <div className="sa-input-group">
                  <label className="sa-label">CITY</label>
                  <input className="sa-input" type="text" placeholder="City" value={form.city} onChange={e => upForm('city', e.target.value)} required />
                </div>
                <div className="sa-input-group">
                  <label className="sa-label">PINCODE</label>
                  <input className="sa-input" type="text" maxLength={6} placeholder="6-digit pincode" value={form.pincode} onChange={e => upForm('pincode', e.target.value.replace(/\D/,''))} required />
                </div>
              </div>
              <button className="sa-submit" type="submit" disabled={loading}>
                {loading ? 'Creating store…' : '🚀 Start Selling'}
              </button>
            </form>
          )}

        </div>
      </div>
    </div>
  );
};

export default SellerAuth;